import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NocActivity } from '@/mocks/superAdminDashboard';

interface NocActivityFeedProps {
  activities: NocActivity[];
}

const tabConfig: { key: NocActivity['type'] | 'todas'; label: string; icon: string }[] = [
  { key: 'todas', label: 'Todas', icon: 'ri-apps-line' },
  { key: 'radio_registrada', label: 'Radios', icon: 'ri-radio-line' },
  { key: 'pago', label: 'Pagos', icon: 'ri-money-dollar-circle-line' },
  { key: 'aprobacion', label: 'Aprobaciones', icon: 'ri-check-double-line' },
  { key: 'ticket', label: 'Tickets', icon: 'ri-customer-service-2-line' },
  { key: 'error', label: 'Errores', icon: 'ri-error-warning-line' },
  { key: 'broadcast', label: 'Broadcasts', icon: 'ri-global-line' },
];

const typeAccent: Record<string, { icon: string; bg: string; color: string }> = {
  radio_registrada: { icon: 'ri-radio-button-line', bg: 'bg-primary-100', color: 'text-primary-600' },
  pago:               { icon: 'ri-money-dollar-circle-line', bg: 'bg-emerald-100', color: 'text-emerald-600' },
  aprobacion:         { icon: 'ri-check-double-line', bg: 'bg-accent-100', color: 'text-accent-600' },
  ticket:             { icon: 'ri-customer-service-2-line', bg: 'bg-amber-100', color: 'text-amber-600' },
  error:              { icon: 'ri-error-warning-line', bg: 'bg-red-100', color: 'text-red-500' },
  broadcast:          { icon: 'ri-global-line', bg: 'bg-secondary-100', color: 'text-secondary-600' },
};

export default function NocActivityFeed({ activities }: NocActivityFeedProps) {
  const [activeTab, setActiveTab] = useState<NocActivity['type'] | 'todas'>('todas');
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const filtered = activeTab === 'todas'
    ? activities
    : activities.filter((a) => a.type === activeTab);

  const displayed = expanded ? filtered : filtered.slice(0, 5);
  const hasMore = filtered.length > 5;

  const handleRowClick = (activity: NocActivity) => {
    switch (activity.type) {
      case 'radio_registrada': navigate('/sa/radios'); break;
      case 'pago': navigate('/sa/payments'); break;
      case 'aprobacion': navigate('/sa/radio-approvals'); break;
      case 'ticket': navigate('/sa/support'); break;
      case 'error': navigate('/sa/system-health'); break;
      case 'broadcast': navigate('/sa/broadcast-center'); break;
    }
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-background-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-history-line text-foreground-500 text-sm"></i>
            </div>
            <div>
              <h2 className="text-foreground-950 font-semibold text-sm">Actividad Reciente — NOC</h2>
              <p className="text-foreground-400 text-[10px]">Últimas operaciones en la plataforma</p>
            </div>
          </div>
          <span className="flex items-center gap-1.5 text-foreground-400 text-[10px] bg-background-100 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
            Actualizado ahora
          </span>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none flex-wrap">
          {tabConfig.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setExpanded(false); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Activities */}
      <div className="divide-y divide-background-200">
        {displayed.length === 0 ? (
          <div className="flex flex-col items-center text-center gap-2 py-10">
            <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-inbox-line text-foreground-400 text-lg"></i>
            </div>
            <p className="text-foreground-800 text-xs font-semibold">Sin actividad</p>
            <p className="text-foreground-400 text-[10px]">No hay eventos de este tipo aún.</p>
          </div>
        ) : (
          displayed.map((activity, idx) => {
            const cfg = typeAccent[activity.type];
            return (
              <div
                key={activity.id}
                className="flex items-center gap-3 px-5 py-3 hover:bg-background-100 transition-colors cursor-pointer group"
                style={{ animation: `fadeInUp 0.25s ease-out forwards`, animationDelay: `${idx * 30}ms`, opacity: 0 }}
                onClick={() => handleRowClick(activity)}
              >
                {/* Type icon */}
                <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                  <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-foreground-800 text-xs font-semibold">{activity.title}</span>
                    <span className="text-foreground-500 text-[10px]">{activity.description}</span>
                  </div>
                  <p className="text-foreground-600 text-[10px] font-medium mt-0.5">{activity.entity}</p>
                </div>

                {/* Metadata */}
                {activity.metadata && (
                  <span className="px-1.5 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[9px] font-bold whitespace-nowrap shrink-0 hidden sm:block">
                    {activity.metadata}
                  </span>
                )}

                {/* Time */}
                <span className="text-foreground-400 text-[9px] whitespace-nowrap shrink-0">{activity.timeRelative}</span>
              </div>
            );
          })
        )}

        {/* Show more / less */}
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full py-2.5 text-foreground-400 text-[10px] font-medium hover:text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            {expanded ? 'Ver menos' : `Ver todas (${filtered.length})`}
          </button>
        )}
      </div>
    </div>
  );
}